import type { ButtonHTMLAttributes, Ref } from 'react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { CaretUpDownIcon, CheckIcon } from '@phosphor-icons/react'

export interface SearchableRecordOption {
  value: string
  label: string
  description?: string | null
  /** Extra text the client-side filter matches when no server search is wired. */
  searchText?: string
}

export interface SearchableRecordSelectProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'value'
> {
  ref?: Ref<HTMLButtonElement>
  value?: string
  onValueChange: (value: string) => void
  options: SearchableRecordOption[]
  placeholder: string
  searchPlaceholder: string
  emptyMessage: string
  /**
   * A fixed first row such as "No project" or "All projects". It is always
   * listed regardless of the search term and is selected when `value`
   * equals its value.
   */
  emptyOption?: { value: string; label: string }
  /**
   * Server-side search. When `onSearchChange` is provided the picker stops
   * filtering `options` itself: the parent owns the search term, passes it
   * to the API and hands back the matching page as `options`. The picker
   * hooks in `@/hooks/useRecordPicker` supply these three props.
   */
  search?: string
  onSearchChange?: (search: string) => void
  isSearching?: boolean
}

/**
 * Single-value combobox for picking a record (project, client, deal, task).
 * Pair it with a `use*Picker` hook to search on the server instead of
 * loading the whole list; on its own it filters the given options locally.
 */
export function SearchableRecordSelect({
  ref,
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  emptyOption,
  search: controlledSearch,
  onSearchChange,
  isSearching = false,
  className,
  'aria-label': ariaLabel,
  ...triggerProps
}: SearchableRecordSelectProps) {
  const [open, setOpen] = useState(false)
  const [localSearch, setLocalSearch] = useState('')
  const isServerSearch = typeof onSearchChange === 'function'
  const search = isServerSearch ? (controlledSearch ?? '') : localSearch
  const setSearch = (next: string) => {
    if (isServerSearch) onSearchChange(next)
    else setLocalSearch(next)
  }

  const selectedOption =
    (emptyOption && value === emptyOption.value
      ? { value: emptyOption.value, label: emptyOption.label }
      : null) ??
    options.find(option => option.value === value) ??
    null

  const filteredOptions = (() => {
    const query = search.trim().toLowerCase()
    if (isServerSearch || !query) return options

    return options.filter(option => {
      const haystack = [
        option.label,
        option.description,
        option.searchText,
        option.value,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return haystack.includes(query)
    })
  })()

  const renderItem = (option: SearchableRecordOption, onPick: () => void) => (
    <CommandItem
      key={option.value}
      value={option.value}
      onSelect={onPick}
      className="gap-2"
    >
      <CheckIcon
        className={cn(
          'h-3.5 w-3.5 shrink-0',
          option.value === value ? 'opacity-100' : 'opacity-0',
        )}
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm">{option.label}</span>
        {option.description && (
          <span className="truncate text-[11px] text-muted-foreground">
            {option.description}
          </span>
        )}
      </span>
    </CommandItem>
  )

  const pick = (next: string) => {
    onValueChange(next)
    setOpen(false)
    setSearch('')
  }

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch('')
      }}
      modal={true}
    >
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-10 w-full justify-between px-3 font-normal',
            !selectedOption && 'text-muted-foreground',
            className,
          )}
          {...triggerProps}
          // An aria-label replaces the button's text, so the choice (or the
          // placeholder) goes into the name too: "Client: Acme Ltd".
          aria-label={
            ariaLabel
              ? `${ariaLabel}: ${selectedOption?.label ?? placeholder ?? 'none'}`
              : undefined
          }
        >
          {selectedOption ? (
            <span className="flex min-w-0 flex-col items-start text-left">
              <span className="w-full truncate text-sm font-medium text-foreground">
                {selectedOption.label}
              </span>
              {'description' in selectedOption &&
                selectedOption.description && (
                  <span className="w-full truncate text-[11px] text-muted-foreground">
                    {selectedOption.description}
                  </span>
                )}
            </span>
          ) : (
            <span className="truncate">{placeholder}</span>
          )}
          <CaretUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        padding="none"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>
              {isSearching ? 'Searching…' : emptyMessage}
            </CommandEmpty>
            <CommandGroup>
              {emptyOption &&
                renderItem(
                  { value: emptyOption.value, label: emptyOption.label },
                  () => pick(emptyOption.value),
                )}
              {filteredOptions.map(option =>
                renderItem(option, () => pick(option.value)),
              )}
            </CommandGroup>
            {/* cmdk only shows CommandEmpty when there are no items at all,
                and the fixed empty row is always an item. */}
            {emptyOption && filteredOptions.length === 0 && (
              <div className="py-4 text-center text-sm text-muted-foreground">
                {isSearching ? 'Searching…' : emptyMessage}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
